import { Loader2 } from "lucide-react";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { ActiveOrganizationWageSet } from "@/features/organization-wage-sets/hooks/use-active-organization-wage-sets";
import { OrganizationWageSetOptionLabel } from "@/features/time-entry/components/OrganizationWageSetOptionLabel";
import { OfferWageSource } from "@/features/job-offer/utils/job-offer-data";

interface WageSourceFieldsProps {
  wageSource: OfferWageSource;
  onWageSourceChange: (wageSource: OfferWageSource) => void;
  organizationWageSetId: string | null | undefined;
  onOrganizationWageSetIdChange: (organizationWageSetId: string) => void;
  organizationWageSets: ActiveOrganizationWageSet[];
  isLoadingOrganizationWageSets: boolean;
  error?: string;
}

/** Choice between using a wage set and setting wages on the offer itself. */
export function WageSourceFields({
  wageSource,
  onWageSourceChange,
  organizationWageSetId,
  onOrganizationWageSetIdChange,
  organizationWageSets,
  isLoadingOrganizationWageSets,
  error,
}: WageSourceFieldsProps) {
  const selectedOrganizationWageSet = organizationWageSets.find(
    (orgWageSet) => orgWageSet.organizationWageSetId === organizationWageSetId,
  );

  return (
    <div className="space-y-3">
      <RadioGroup
        value={wageSource}
        onValueChange={(value) => onWageSourceChange(value as OfferWageSource)}
        className="flex flex-col gap-2 sm:flex-row sm:gap-6"
        aria-label="Where this offer's wages come from"
      >
        <div className="flex items-center gap-2">
          <RadioGroupItem
            id="wage-source-custom"
            value={OfferWageSource.Custom}
          />
          <Label htmlFor="wage-source-custom">Set wages for this offer</Label>
        </div>
        <div className="flex items-center gap-2">
          <RadioGroupItem
            id="wage-source-wage-set"
            value={OfferWageSource.OrganizationWageSet}
          />
          <Label htmlFor="wage-source-wage-set">Use a wage set</Label>
        </div>
      </RadioGroup>

      {wageSource === OfferWageSource.OrganizationWageSet && (
        <div className="space-y-2">
          {isLoadingOrganizationWageSets && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading wage sets...
            </div>
          )}
          {!isLoadingOrganizationWageSets &&
            organizationWageSets.length === 0 && (
              <p className="text-sm text-slate-600">
                This business unit has no active wage sets. Create one on the
                wage sets page, or set wages for this offer.
              </p>
            )}
          {organizationWageSets.length > 0 && (
            <Select
              value={organizationWageSetId ?? ""}
              onValueChange={onOrganizationWageSetIdChange}
            >
              <SelectTrigger
                aria-label="Wage set"
                className="h-auto min-h-[56px] py-2 [&>span]:line-clamp-none"
              >
                <SelectValue placeholder="Select a wage set">
                  {selectedOrganizationWageSet && (
                    <OrganizationWageSetOptionLabel
                      name={selectedOrganizationWageSet.name}
                      wages={selectedOrganizationWageSet.wages}
                    />
                  )}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {organizationWageSets.map((orgWageSet) => (
                  <SelectItem
                    key={orgWageSet.organizationWageSetId}
                    value={orgWageSet.organizationWageSetId}
                  >
                    <OrganizationWageSetOptionLabel
                      name={orgWageSet.name}
                      wages={orgWageSet.wages}
                    />
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <p className="text-sm text-slate-500">
            The offer keeps the wage set's current wages, even if the wage set
            changes later.
          </p>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      )}
    </div>
  );
}
